// Print-to-PDF helper.
//
// No PDF library involved: the browser's own print pipeline produces the file,
// so the output keeps real selectable text and costs nothing in bundle size.
// The caller marks the subtree it wants with `pdfPrintArea` and everything else
// is hidden by the @media print rules in styles/layout/_printPdf.scss.
//
// The suggested filename comes from document.title in every major browser, so we
// swap it for the duration of the print and restore it afterwards.

const PRINT_MODE_CLASS = "pdfPrintMode";
const PRINT_AREA_CLASS = "pdfPrintArea";
// Marks every ancestor between <body> and the print area. The stylesheet uses
// it to hide each of their OTHER children, which leaves the report as the only
// thing on the page while keeping it in normal flow.
//
// Hiding the rest with `visibility` and lifting the report out with
// position:absolute looks equivalent and is not: the app shell sets
// position:relative on its containers (%relBlock), so an absolutely positioned
// report resolves against the sidebar-offset container and prints as a narrow
// column, and the invisible-but-still-laid-out chrome adds blank pages.
const PRINT_ANCESTOR_CLASS = "pdfPrintAncestor";

// Browsers disagree on whether `afterprint` fires when the user cancels the
// dialog, and Safari has historically skipped it entirely. The backup is the
// window regaining focus: the print dialog is app-modal, so closing it —
// printed or cancelled — refocuses the window. A timer is NOT safe here:
// where print() does not block (Safari), it would strip the classes while the
// dialog is still open and the preview would re-render as the whole app shell.
// Whichever signal lands first wins; cleanup is idempotent.

const sanitiseFileName = (name = "") =>
  String(name)
    .replace(/[\\/:*?"<>|]/g, "") // characters browsers reject in a download name
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);

/**
 * Open the print dialog for the current `.pdfPrintArea` subtree.
 *
 * @param {string} fileName Suggested PDF name, without extension.
 */
const printPdf = (fileName = "") => {
  const originalTitle = document.title;
  const cleanName = sanitiseFileName(fileName);
  const marked = [];
  let done = false;

  const cleanup = () => {
    if (done) return;
    done = true;
    marked.forEach((el) => el.classList.remove(PRINT_ANCESTOR_CLASS));
    marked.length = 0;
    document.body.classList.remove(PRINT_MODE_CLASS);
    document.title = originalTitle;
    window.removeEventListener("afterprint", cleanup);
    window.removeEventListener("focus", cleanup);
  };

  // Walk from the print area up to <body>, tagging the chain.
  const area = document.querySelector(`.${PRINT_AREA_CLASS}`);
  if (!area) {
    // Nothing marked as printable: printing now would dump the whole app shell
    // into the PDF, which is worse than doing nothing.
    return;
  }
  for (let el = area.parentElement; el && el !== document.body; el = el.parentElement) {
    el.classList.add(PRINT_ANCESTOR_CLASS);
    marked.push(el);
  }

  if (cleanName) {
    document.title = cleanName;
  }
  document.body.classList.add(PRINT_MODE_CLASS);
  window.addEventListener("afterprint", cleanup);
  // Registered before print(): where print() blocks (Chrome), the focus event
  // queued during the dialog is delivered right after it; where it doesn't
  // (Safari), the listener is already in place when the dialog closes.
  window.addEventListener("focus", cleanup);

  window.print();
};

export { printPdf, PRINT_MODE_CLASS };
export default printPdf;
